import useAuthGate from '../hooks/useAuthGate';
import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Loader2, Grid3x3, Star, Bookmark, Flame, Church, BadgeCheck, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { postsApi, mediaUrl, renderBadgeImage } from '../services/postsApi';
import { storiesApi } from '../services/storiesApi';
import { messagesApi } from '../services/messagesApi';

const CHURCH_API = `${process.env.REACT_APP_BACKEND_URL}/api/churches`;

const Stat = ({ n, label }) => (
  <div className="text-center">
    <p className="text-lg font-bold leading-none">{n ?? 0}</p>
    <p className="text-[11px] text-muted-foreground">{label}</p>
  </div>
);

const ProfileSheet = ({ userId, open, onClose, language = 'en', onOpenPost }) => {
  const es = language === 'es';
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyFollow, setBusyFollow] = useState(false);
  const [lightbox, setLightbox] = useState(null); // snapshot
  const [tab, setTab] = useState('posts');
  const [churches, setChurches] = useState([]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true); setTab('posts');
    (async () => {
      try {
        const [p, list, hl] = await Promise.all([
          postsApi.profile(userId), postsApi.userPosts(userId), storiesApi.highlights(userId),
        ]);
        if (cancelled) return;
        setProfile(p); setPosts(list); setHighlights(hl);
        if (p.is_me) { try { setSaved(await storiesApi.saved()); } catch { setSaved([]); } }
        try {
          const { data } = await axios.get(`${CHURCH_API}/user/${userId}`, { withCredentials: true });
          if (!cancelled) setChurches(data.churches || []);
        } catch { if (!cancelled) setChurches([]); }
      } catch { if (!cancelled) toast.error(es ? 'No se pudo cargar el perfil' : 'Could not load profile'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, userId, es]);

  const { requireAuth, dialog: authDialog } = useAuthGate();
  const toggleFollow = async () => {
    if (!profile) return;
    if (!requireAuth(es ? 'Seguir personas' : 'Following people', { type: 'follow_user', target: userId })) return;
    setBusyFollow(true);
    const wasFollowing = profile.is_following;
    setProfile((p) => ({ ...p, is_following: !wasFollowing, followers_count: p.followers_count + (wasFollowing ? -1 : 1) }));
    try {
      if (wasFollowing) await postsApi.unfollow(userId); else await postsApi.follow(userId);
    } catch (e) {
      setProfile((p) => ({ ...p, is_following: wasFollowing, followers_count: p.followers_count + (wasFollowing ? 1 : -1) }));
      toast.error(e?.response?.data?.detail || (es ? 'Error' : 'Something went wrong'));
    } finally { setBusyFollow(false); }
  };

  const [busyMsg, setBusyMsg] = useState(false);
  const messageUser = async () => {
    setBusyMsg(true);
    try {
      const { thread_id } = await messagesApi.openDirect(userId);
      onClose?.();
      setTimeout(() => window.dispatchEvent(new CustomEvent('lumera:open-messages', { detail: { focus: { openThreadId: thread_id } } })), 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo abrir el chat' : 'Could not open chat'));
    } finally { setBusyMsg(false); }
  };

  const initials = (profile?.name || '?').slice(0, 2).toUpperCase();

  const shareBadge = async (b) => {
    // Own-profile re-share: the partner's name isn't stored on the badge, so
    // omit the "with ..." line for shared badges rather than showing self.
    const blob = await renderBadgeImage(b.days, b.kind, '', es);
    onClose?.();
    setTimeout(() => window.dispatchEvent(new CustomEvent('lumera:milestone-share', { detail: {
      kind: 'streak', direct: true, imageBlob: blob,
      message: es ? '¿Compartir tu insignia?' : 'Share your badge?',
    }})), 200);
  };
  const gridItems = tab === 'saved' ? saved.map((s) => ({ key: s.id, img: s.snapshot.image_url, snap: s.snapshot })) : posts.map((p) => ({ key: p.id, img: p.image_url, post: p }));

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="profile-sheet">
        <SheetHeader><SheetTitle>{es ? 'Perfil' : 'Profile'}</SheetTitle><SheetDescription className="sr-only">{es ? 'Publicaciones y estadísticas del perfil.' : 'Profile posts and stats.'}</SheetDescription></SheetHeader>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : profile ? (
          <div className="space-y-5 pt-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-20 h-20">
                {profile.picture ? <AvatarImage src={profile.picture} /> : null}
                <AvatarFallback className="text-xl bg-primary/15">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 flex items-center justify-around">
                <Stat n={profile.posts_count} label={es ? 'Posts' : 'Posts'} />
                <Stat n={profile.followers_count} label={es ? 'Seguidores' : 'Followers'} />
                <Stat n={profile.following_count} label={es ? 'Siguiendo' : 'Following'} />
              </div>
            </div>
            <div>
              <p className="font-semibold">{profile.name}</p>
              {profile.username && <p className="text-sm text-muted-foreground" data-testid="profile-username">@{profile.username}</p>}
              {!profile.is_me && (
                <div className="mt-2 flex gap-2">
                  <Button onClick={toggleFollow} disabled={busyFollow} className="flex-1"
                    variant={profile.is_following ? 'outline' : 'default'} data-testid="profile-follow-btn">
                    {busyFollow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {profile.is_following ? (es ? 'Siguiendo' : 'Following') : (es ? 'Seguir' : 'Follow')}
                  </Button>
                  <Button onClick={messageUser} disabled={busyMsg} variant="outline" className="flex-1" data-testid="profile-message-btn">
                    {busyMsg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                    {es ? 'Mensaje' : 'Message'}
                  </Button>
                </div>
              )}
            </div>

            {/* Streak milestone badges */}
            {(profile.badges || []).length > 0 && (
              <div data-testid="profile-badges">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-t pt-3 mb-2 uppercase tracking-widest">
                  <Flame className="w-3.5 h-3.5 text-orange-500" /> {es ? 'Logros de oración' : 'Prayer milestones'}
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.badges.map((b) => (
                    <button
                      key={b.badge_id}
                      onClick={profile.is_me ? () => shareBadge(b) : undefined}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${profile.is_me ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default'} ${b.kind === 'partner' ? 'bg-gradient-to-r from-rose-500/15 to-purple-500/15 text-purple-600 dark:text-purple-300' : 'bg-gradient-to-r from-orange-500/15 to-amber-500/15 text-orange-600 dark:text-orange-400'}`}
                      title={profile.is_me ? (es ? 'Toca para compartir' : 'Tap to share') : b.label}
                      data-testid={`profile-badge-${b.badge_id}`}
                    >
                      {b.kind === 'partner' ? '🤝' : '🔥'} {b.days} {es ? 'días' : 'days'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <div data-testid="profile-highlights">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-t pt-3 mb-2 uppercase tracking-widest">
                  <Star className="w-3.5 h-3.5" /> {es ? 'Destacadas' : 'Highlights'}
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {highlights.map((h) => (
                    <button key={h.id} onClick={() => setLightbox(h.snapshot)} className="shrink-0 flex flex-col items-center gap-1" data-testid={`profile-highlight-${h.id}`}>
                      <span className="w-16 h-16 rounded-full p-[2.5px] bg-gradient-to-tr from-amber-400 to-fuchsia-500 block">
                        <img src={mediaUrl(h.snapshot.image_url)} alt="" className="w-full h-full rounded-full object-cover border-2 border-background" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Churches the user belongs to */}
            {churches.length > 0 && (
              <div className="border-t pt-3" data-testid="profile-churches">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2 uppercase tracking-widest">
                  <Church className="w-3.5 h-3.5" /> {es ? 'Iglesias' : 'Churches'}
                </div>
                <div className="space-y-2">
                  {churches.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-sm" data-testid={`profile-church-${c.id}`}>
                      <Avatar className="w-7 h-7">
                        {c.logo_url ? <AvatarImage src={c.logo_url} alt={c.name} /> : null}
                        <AvatarFallback className="bg-primary/10 text-primary"><Church className="w-3.5 h-3.5" /></AvatarFallback>
                      </Avatar>
                      <span className="font-medium truncate">{c.name}</span>
                      {c.verified && <BadgeCheck className="w-3.5 h-3.5 text-sky-500 shrink-0" />}
                      {c.city && <span className="text-xs text-muted-foreground truncate">· {c.city}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tabs: Posts / Saved (self only) */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-center gap-6 mb-3">
                <button onClick={() => setTab('posts')} className={`flex items-center gap-1.5 text-xs uppercase tracking-widest ${tab === 'posts' ? 'text-foreground font-semibold' : 'text-muted-foreground'}`} data-testid="profile-tab-posts">
                  <Grid3x3 className="w-3.5 h-3.5" /> {es ? 'Publicaciones' : 'Posts'}
                </button>
                {profile.is_me && (
                  <button onClick={() => setTab('saved')} className={`flex items-center gap-1.5 text-xs uppercase tracking-widest ${tab === 'saved' ? 'text-foreground font-semibold' : 'text-muted-foreground'}`} data-testid="profile-tab-saved">
                    <Bookmark className="w-3.5 h-3.5" /> {es ? 'Guardadas' : 'Saved'}
                  </button>
                )}
              </div>
              {gridItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">{tab === 'saved' ? (es ? 'Nada guardado aún' : 'Nothing saved yet') : (es ? 'Aún no hay publicaciones' : 'No posts yet')}</p>
              ) : (
                <div className="grid grid-cols-3 gap-1" data-testid={tab === 'saved' ? 'profile-saved-grid' : 'profile-post-grid'}>
                  {gridItems.map((it) => (
                    <button key={it.key} onClick={() => it.post ? onOpenPost?.(it.post) : setLightbox(it.snap)} className="aspect-square overflow-hidden bg-muted">
                      <img src={mediaUrl(it.img)} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.closest('button').style.display = 'none'; }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </SheetContent>

      {/* Snapshot lightbox for highlights + saved */}
      <Dialog open={!!lightbox} onOpenChange={(v) => { if (!v) setLightbox(null); }}>
        <DialogContent className="max-w-md p-2" data-testid="profile-snapshot-lightbox">
          <DialogTitle className="sr-only">{es ? 'Historia destacada' : 'Highlighted story'}</DialogTitle>
          <DialogDescription className="sr-only">{es ? 'Vista de historia guardada' : 'Saved story view'}</DialogDescription>
          {lightbox && (
            <div>
              <img src={mediaUrl(lightbox.image_url)} alt={lightbox.caption || ''} className="w-full rounded-lg" />
              {lightbox.caption && <p className="text-sm text-center mt-2">{lightbox.caption}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {authDialog}
    </Sheet>
  );
};

export default ProfileSheet;
